import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from './axiosBaseQuery';
import { onboardingApi } from './onboardingApi';
import { accountApi } from './accountApi';
import {
  CreatePassTemplateRequest,
  CreatePassTemplateResponse,
  UpdatePassTemplateRequest,
  ListPassTemplatesResponse,
  GetPassTemplateResponse,
} from '../../types/template';

const buildQuery = (
  params: Record<string, string | number | boolean | undefined>
) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== '') qs.append(k, String(v));
  });
  const s = qs.toString();
  return s ? `?${s}` : '';
};

// Onboarding step 1 is "template created" — invalidate to fire the completion
// modal, and refetch /me so flags evaluated on first template creation
// (first_pass_improvements_enabled) are fresh when the modal renders.
const invalidateOnboarding = async (
  _arg: unknown,
  {
    dispatch,
    queryFulfilled,
  }: {
    dispatch: (action: unknown) => void;
    queryFulfilled: Promise<unknown>;
  }
) => {
  try {
    await queryFulfilled;
    dispatch(onboardingApi.util.invalidateTags(['Onboarding']));
    dispatch(accountApi.util.invalidateTags(['Account']));
  } catch {
    // Mutation failed — nothing to invalidate.
  }
};

export const templateApi = createApi({
  reducerPath: 'templateApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['PassTemplates'],
  endpoints: (builder) => ({
    getPassTemplate: builder.query<
      GetPassTemplateResponse,
      {
        id: string;
        fields?: string;
        includeDynamicForm?: boolean;
        includePassesCount?: boolean;
      }
    >({
      query: ({ id, fields, includeDynamicForm, includePassesCount }) => {
        const qs = buildQuery({
          fields,
          includeDynamicForm,
          includePassesCount,
        });
        return `api/v1/pass-templates/${id}${qs}`;
      },
      providesTags: (_result, _error, { id }) => [
        { type: 'PassTemplates', id },
      ],
    }),
    listPassTemplates: builder.query<
      ListPassTemplatesResponse,
      {
        entityId?: string;
        page?: number;
        perPage?: number;
        templateType?: string;
        eventName?: string;
        status?: string;
        search?: string;
        fields?: string;
        sortBy?: string;
        sortOrder?: string;
        skipImages?: boolean;
      }
    >({
      query: ({ entityId, ...params }) => ({
        url: entityId
          ? `api/v1/entities/${entityId}/pass-templates`
          : 'api/v1/pass-templates',
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...(result.data?.map(({ id }) => ({
                type: 'PassTemplates' as const,
                id,
              })) ?? []),
              { type: 'PassTemplates', id: 'LIST' },
            ]
          : [{ type: 'PassTemplates', id: 'LIST' }],
    }),
    listPassTemplatesInfinite: builder.query<
      ListPassTemplatesResponse,
      {
        entityId?: string;
        page?: number;
        perPage?: number;
        search?: string;
        fields?: string;
        skipImages?: boolean;
      }
    >({
      query: ({ entityId, ...params }) => ({
        url: entityId
          ? `api/v1/entities/${entityId}/pass-templates`
          : 'api/v1/pass-templates',
        params,
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        const { page, ...rest } = queryArgs;
        return rest;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (!arg.page || arg.page === 1) {
          currentCache.data = newItems.data;
        } else {
          const existingIds = new Set(currentCache.data.map((item) => item.id));
          for (const item of newItems.data) {
            if (!existingIds.has(item.id)) {
              currentCache.data.push(item);
            }
          }
        }
        if (newItems.meta) {
          currentCache.meta = newItems.meta;
        }
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.page !== previousArg?.page;
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.data?.map(({ id }) => ({
                type: 'PassTemplates' as const,
                id,
              })) ?? []),
              { type: 'PassTemplates', id: 'LIST' },
            ]
          : [{ type: 'PassTemplates', id: 'LIST' }],
    }),
    createPassTemplate: builder.mutation<
      CreatePassTemplateResponse,
      {
        entityId?: string;
        baseTemplateUuid?: string;
        presetId?: string;
      } & CreatePassTemplateRequest
    >({
      query: ({ entityId, baseTemplateUuid, presetId, ...body }) => {
        const paramEntries = {
          ...(baseTemplateUuid !== undefined ? { baseTemplateUuid } : {}),
          ...(presetId !== undefined ? { presetId } : {}),
        };
        const params =
          Object.keys(paramEntries).length > 0 ? paramEntries : undefined;
        return {
          url: entityId
            ? `api/v1/entities/${entityId}/pass-templates`
            : `api/v1/pass-templates`,
          method: 'POST',
          data: body,
          params,
        };
      },
      invalidatesTags: [{ type: 'PassTemplates', id: 'LIST' }],
      onQueryStarted: invalidateOnboarding,
    }),
    updatePassTemplate: builder.mutation<
      CreatePassTemplateResponse,
      {
        id: string;
        data: UpdatePassTemplateRequest;
        includeDynamicForm?: boolean;
      }
    >({
      query: ({ id, data, includeDynamicForm }) => ({
        url: `api/v1/pass-templates/${id}`,
        method: 'PATCH',
        data,
        params:
          includeDynamicForm !== undefined ? { includeDynamicForm } : undefined,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PassTemplates', id },
        { type: 'PassTemplates', id: 'LIST' },
      ],
    }),
    publishPassTemplate: builder.mutation<
      CreatePassTemplateResponse,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `api/v1/pass-templates/${id}/publish`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PassTemplates', id },
        { type: 'PassTemplates', id: 'LIST' },
      ],
      onQueryStarted: invalidateOnboarding,
    }),
    deletePassTemplate: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `api/v1/pass-templates/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'PassTemplates', id: 'LIST' }],
    }),
    archivePassTemplate: builder.mutation<void, { id: string }>({
      query: ({ id }) => ({
        url: `api/v1/pass-templates/${id}/archive`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'PassTemplates', id },
        { type: 'PassTemplates', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetPassTemplateQuery,
  useLazyGetPassTemplateQuery,
  useListPassTemplatesQuery,
  useListPassTemplatesInfiniteQuery,
  useCreatePassTemplateMutation,
  useUpdatePassTemplateMutation,
  usePublishPassTemplateMutation,
  useDeletePassTemplateMutation,
  useArchivePassTemplateMutation,
} = templateApi;
